import { defineStore } from "pinia";
import { inject, ref } from "vue";
import { Mutex } from "async-mutex";
import { HomemoverApiClient } from "@/services/HomemoverApiClient";
import { UserDetails } from '@/types/user/UserDetails';
import { useRouter } from 'vue-router';
import { displayErrorPage } from "@/helpers/errorhandling/ErrorDisplayer";

/** Cache of User details */
export const useUserStore = defineStore('userStore', () => {
    const mutex = new Mutex();
    const cachedUserDetails = ref<UserDetails>();
    const homemoverApiClient = inject('HomemoverApiClient') as HomemoverApiClient;
    const cachedUserId = ref('');
    const router = useRouter();

    const updateUserId = (userId: string) => cachedUserId.value = userId;

    const getCurrentUserDetails = async () => {
        const currentUser = await mutex.runExclusive(async () => {
            if (!cachedUserDetails.value) {
                try {
                    cachedUserDetails.value = await getCurrentUserFromApi();
                } catch (err) {
                    displayErrorPage(router, err);
                }
            }

            //for testing reasons always renew

            return cachedUserDetails.value
        });

        return currentUser;
    };

    /** Gets the user's details from the Homemover Api */
    const getCurrentUserFromApi = async () => {
        return await homemoverApiClient.httpPost<UserDetails>(`app/user`, {
            switchOverId: cachedUserId.value
        });
    }

    /** Checks whether the current user has already selected an energy deal */
    const hasUserSelectedEnergyDeal = async () => {
        // To prevent any errors, ensure we have the user's details
        if (!cachedUserDetails.value) {
            await getCurrentUserDetails();
        }

        if (!cachedUserDetails.value?.energyRegistrationObject) {
            return false;
        }

        return cachedUserDetails.value.energyRegistrationObject.status.toUpperCase() != 'SENT TO TENANT COMMUNITY';
    };

    /** Resets the cached user details so they will be retrieved from the api the next time getCurrentUserDetails is called */
    const clearCurrentUserDetails = () => {
        cachedUserDetails.value = undefined;
    }

    return { getCurrentUserDetails, updateUserId, hasUserSelectedEnergyDeal, clearCurrentUserDetails }
});